import { Plus, Video, X, GripVertical } from "lucide-react";

/**
 * RecentVideosEditor - Manages YouTube video URLs for the homepage video playlist.
 * Only requires a YouTube URL. Thumbnails are auto-generated.
 */
const RecentVideosEditor = ({ videos, sectionTitle, onChange, onTitleChange }) => {
  const getVideoId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
    return match ? match[1] : null;
  };

  const addVideo = () => {
    onChange([...videos, { url: "" }]);
  };

  const updateVideo = (idx, value) => {
    const newVideos = [...videos];
    newVideos[idx] = { ...newVideos[idx], url: value };
    onChange(newVideos);
  };

  const removeVideo = (idx) => {
    onChange(videos.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-bold text-white uppercase tracking-wider">Video Playlist</h4>
        <button
          onClick={addVideo}
          className="flex items-center gap-1 text-sm text-orange-500 hover:text-orange-400"
          data-testid="add-video-btn"
        >
          <Plus className="w-4 h-4" /> Add Video
        </button>
      </div>

      <p className="text-xs text-zinc-500">
        Paste YouTube URLs. Only thumbnails will show on the homepage — clicking opens the video in a lightbox.
      </p>

      <div className="space-y-1">
        <label className="block text-xs text-zinc-500 uppercase">Section Title</label>
        <input
          type="text"
          value={sectionTitle || ""}
          onChange={(e) => onTitleChange(e.target.value)}
          placeholder="Recent Videos"
          className="w-full bg-zinc-900 border border-zinc-700 text-white px-3 py-2 text-sm rounded focus:border-orange-500/50 focus:outline-none"
          data-testid="videos-section-title-input"
        />
      </div>

      {videos.length === 0 ? (
        <div className="text-center py-6 bg-zinc-800/50 rounded border border-dashed border-zinc-700">
          <Video className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
          <p className="text-zinc-500 text-sm">No videos added</p>
          <p className="text-zinc-600 text-xs">Auto-fetched recordings will be used instead</p>
        </div>
      ) : (
        <div className="space-y-2">
          {videos.map((video, idx) => {
            const videoId = getVideoId(video.url);
            return (
              <div key={idx} className="flex items-center gap-3 p-2 bg-zinc-800/50 border border-zinc-700 rounded group">
                <GripVertical className="w-4 h-4 text-zinc-600 shrink-0" />
                <div className="w-20 h-12 bg-zinc-900 shrink-0 overflow-hidden rounded">
                  {videoId ? (
                    <img
                      src={`https://img.youtube.com/vi/${videoId}/mqdefault.jpg`}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-zinc-600">
                      <Video className="w-5 h-5" />
                    </div>
                  )}
                </div>
                <input
                  type="text"
                  value={video.url || ""}
                  onChange={(e) => updateVideo(idx, e.target.value)}
                  placeholder="https://youtube.com/watch?v=..."
                  className="flex-1 bg-zinc-900 border border-zinc-700 text-white px-3 py-2 text-sm rounded focus:border-orange-500/50 focus:outline-none"
                  data-testid={`video-url-input-${idx}`}
                />
                <button
                  onClick={() => removeVideo(idx)}
                  className="p-1.5 text-zinc-600 hover:text-red-400 transition-colors shrink-0"
                  data-testid={`remove-video-${idx}`}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RecentVideosEditor;
